import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AbstractDynamicComponent } from '@components/abstracts';
import { ListingCorpsDeMeuble } from '@components/listing-corps-de-meuble/models';
import { Image } from '@shared/components/image/models';
import { OptionCardComponent } from '@shared/components/option-card';
import { PopinImageComponent } from '@shared/components/popin-image';
import { SvgComponent } from '@shared/components/svg';
import { TitleComponent } from '@shared/components/title';
import { VideoComponent } from '@shared/components/video';
import { DialogService } from '@shared/services/dialog';
import { ElasticSearchService } from '@shared/services/elastic-search';
import { CabinetBody } from '@shared/services/elastic-search/models';
import { tap } from 'rxjs/operators';

type Indice = { current: number; length: number };

@Component({
  selector: 'app-listing-corps-de-meuble',
  imports: [NgTemplateOutlet, OptionCardComponent, TitleComponent, VideoComponent, SvgComponent],
  templateUrl: './listing-corps-de-meuble.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ListingCorpsDeMeubleComponent extends AbstractDynamicComponent<ListingCorpsDeMeuble> {
  protected readonly frontsImagesCurrentIndices = signal<Indice[]>([]);
  protected readonly cabinetBodies = toSignal(
    inject(ElasticSearchService)
      .getCabinetBodies()
      .pipe(tap((cabinetBodies) => this.buildImageIndices(cabinetBodies)))
  );
  private readonly dialogService = inject(DialogService);

  updateImageIndex(cabinetBodyIndex: number, step: -1 | 1): void {
    this.frontsImagesCurrentIndices.update((indices) => {
      const updated = [...indices];

      const currentIndex = updated[cabinetBodyIndex]!.current;
      const length = updated[cabinetBodyIndex]!.length;

      updated[cabinetBodyIndex]!.current = (currentIndex + step + length) % length;

      return updated;
    });
  }

  openVisualFullScreenModal(image: Image): void {
    this.dialogService.openDialog(PopinImageComponent, 'FULL_SCREEN', {
      data: {
        image,
      },
    });
  }

  private buildImageIndices(cabinetBodies: CabinetBody[]): void {
    const imagesCurrentIndices = cabinetBodies.reduce((acc, front) => {
      acc.push({ current: 0, length: front.images.length });
      return acc;
    }, [] as Indice[]);
    this.frontsImagesCurrentIndices.set(imagesCurrentIndices);
  }
}
